import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Typography, Card, Table, Button, Modal, Form, Input, Switch, Space, message, Tooltip } from 'antd';
import { WarningOutlined, TableOutlined } from '@ant-design/icons';
import MainLayout from '../components/MainLayout';
import ItemDetailDrawer from '../components/ItemDetailDrawer';
import { itemService } from '../services/items';
import { documentService } from '../services/documents';
import { tenantService } from '../services/tenants';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { downloadXlsx } from '../utils/excelExport';
import './Dashboard.css';

const { Title } = Typography;

function Chemicals() {
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [missingDocsByItemId, setMissingDocsByItemId] = useState({});
  const [form] = Form.useForm();
  const { selectedTenantId } = useTenant();
  const { user } = useAuth();
  const [tenantMap, setTenantMap] = useState({});
  const [tableSort, setTableSort] = useState(null);
  const isAdmin = user?.role === 'admin';
  const canMutateItems = user?.role !== 'all_external';

  const formatError = (err) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return 'Errore';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d) => d.msg || d.detail || JSON.stringify(d)).join(' | ');
    if (typeof detail === 'object') return detail.msg || detail.detail || JSON.stringify(detail);
    return 'Errore';
  };

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { item_type: 'chemical' };
      if (selectedTenantId) params.tenant_id = selectedTenantId;
      const res = await itemService.list('chemical', params);
      const mapped = (res || []).map((item) => ({
        ...item,
        name: item.standard_fields?.name,
      }));
      setData(mapped);
      checkMissingDocs(mapped);
    } catch (e) {
      message.error('Errore nel caricamento prodotti chimici');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  const loadTenants = useCallback(async () => {
    const canLoadTenants = user?.role === 'admin' || user?.role === 'qhse_nke' || user?.role === 'all_external';
    if (!canLoadTenants) return;
    try {
      const res = await tenantService.list();
      const map = {};
      (res || []).forEach((t) => {
        map[t.id] = t.name;
      });
      setTenantMap(map);
    } catch (e) {
      console.error('Errore caricamento tenant', e);
    }
  }, [user]);

  const checkMissingDocs = async (items) => {
    const missingMap = {};
    const requiredDocs = ['Scheda di sicurezza'];
    
    for (const item of items) {
      // Salta item senza nome
      if (!item.name) continue;
      
      try {
        const docs = await documentService.list({ item_id: item.id });
        const loadedDocTypes = docs.map(d => d.document_type);
        const missing = requiredDocs.filter(reqDoc => !loadedDocTypes.includes(reqDoc));
        if (missing.length > 0) {
          missingMap[item.id] = missing;
        }
      } catch (e) {
        console.error(`Errore caricamento documenti per item ${item.id}:`, e);
      }
    }
    setMissingDocsByItemId(missingMap);
  };

  useEffect(() => {
    loadItems();
    loadTenants();
  }, [selectedTenantId, loadItems, loadTenants]);

  const dataWithTenant = useMemo(() => {
    return (data || []).map((item) => ({
      ...item,
      company_name: tenantMap[item.tenant_id] || '',
    }));
  }, [data, tenantMap]);

  const filteredData = useMemo(() => {
    if (!search) return dataWithTenant;
    const term = search.toLowerCase();
    return dataWithTenant.filter((item) => Object.values(item || {}).some((val) => (val !== null && val !== undefined) && String(val).toLowerCase().includes(term)));
  }, [dataWithTenant, search]);

  const stringSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '');
  const boolSorter = (key) => (a, b) => Number(a[key]) - Number(b[key]);

  const sortComparators = {
    company_name: stringSorter('company_name'),
    name: stringSorter('name'),
    is_active: boolSorter('is_active'),
  };

  const sortedData = useMemo(() => {
    if (!tableSort?.order || !tableSort?.field || !sortComparators[tableSort.field]) {
      return filteredData;
    }
    const arr = [...filteredData];
    arr.sort(sortComparators[tableSort.field]);
    if (tableSort.order === 'descend') arr.reverse();
    return arr;
  }, [filteredData, tableSort]);

  const handleTableChange = (_, __, sorter) => {
    if (sorter && sorter.order) {
      setTableSort({ field: sorter.field || sorter.columnKey, order: sorter.order });
    } else {
      setTableSort(null);
    }
  };

  const handleExportXlsx = () => {
    const headers = ['ID', 'Azienda', 'Nome prodotto', 'Attivo'];
    const rows = (sortedData || []).map((item) => [
      item.id,
      item.company_name || '',
      item.name || '',
      item.is_active ? 'Sì' : 'No',
    ]);
    downloadXlsx('prodotti_chimici.xlsx', headers, rows);
  };

  const onSubmit = async (values) => {
    try {
      const payload = {
        item_type: 'chemical',
        standard_fields: { name: values.name },
        is_active: values.is_active,
      };
      
      // Aggiungi tenant_id se selezionato
      if (selectedTenantId) {
        payload.tenant_id = selectedTenantId;
      }

      if (editing) {
        await itemService.update(editing.id, payload);
        message.success('Prodotto aggiornato');
      } else {
        await itemService.create(payload);
        message.success('Prodotto creato');
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      loadItems();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({ name: record.name, is_active: record.is_active });
    setModalOpen(true);
  };

  const handleToggle = async (record) => {
    const nextStatus = !record.is_active;
    try {
      await itemService.update(record.id, { is_active: nextStatus });
      message.success(nextStatus ? 'Prodotto attivato' : 'Prodotto disattivato');
      loadItems();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleRowClick = (record) => {
    setSelectedItem(record);
    setDrawerOpen(true);
  };

  const handleBulkDisable = async () => {
    try {
      await Promise.all(selectedRowKeys.map(id => itemService.update(id, { is_active: false })));
      message.success('Prodotti disattivati con successo');
      setSelectedRowKeys([]);
      loadItems();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleBulkDelete = () => {
    if (!selectedRowKeys.length) return;
    Modal.confirm({
      title: 'Confermi la rimozione bulk dei prodotti selezionati?',
      okType: 'danger',
      okText: 'Rimuovi',
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          await itemService.bulkDelete(selectedRowKeys);
          message.success('Prodotti rimossi con successo');
          setSelectedRowKeys([]);
          loadItems();
        } catch (e) {
          message.error(formatError(e));
        }
      },
    });
  };

  const rowSelection = isAdmin ? {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  } : null;

  const columns = [
    { title: 'Azienda', dataIndex: 'company_name', key: 'company_name', sorter: stringSorter('company_name'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'company_name' ? tableSort.order : null },
    {
      title: 'Nome prodotto',
      dataIndex: 'name',
      key: 'name',
      sorter: stringSorter('name'),
      sortDirections: ['ascend', 'descend'],
      sortOrder: tableSort?.field === 'name' ? tableSort.order : null,
      render: (text, record) => (
        <Space>
          {missingDocsByItemId[record.id] && (
            <Tooltip title={`Documenti mancanti: ${missingDocsByItemId[record.id].join(', ')}`}>
              <WarningOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
            </Tooltip>
          )}
          <span>{text}</span>
        </Space>
      ),
    },
    { title: 'Attivo', dataIndex: 'is_active', key: 'is_active', render: (v) => (v ? 'Sì' : 'No'), sorter: boolSorter('is_active'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'is_active' ? tableSort.order : null },
    ...(canMutateItems ? [{
      title: 'Azioni',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={(e) => { e.stopPropagation(); handleEdit(record); }}>Modifica</Button>
          <Button size="small" danger={record.is_active} onClick={(e) => { e.stopPropagation(); handleToggle(record); }}>
            {record.is_active ? 'Disattiva' : 'Attiva'}
          </Button>
        </Space>
      ),
    }] : []),
  ];

  return (
    <MainLayout title="Prodotti Chimici">
      <Card style={{ marginBottom: 24 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Title level={2} style={{ margin: 0 }}>Prodotti Chimici</Title>
          <Button icon={<TableOutlined />} onClick={handleExportXlsx}>Esporta Excel</Button>
          <Input.Search
            allowClear
            placeholder="Cerca in tutti i campi"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <Space>
            {isAdmin && selectedRowKeys.length > 0 && (
              <Button danger type="primary" onClick={handleBulkDelete}>
                Elimina selezionati ({selectedRowKeys.length})
              </Button>
            )}
            {canMutateItems && (
              <Button type="primary" size="large" onClick={() => setModalOpen(true)}>+ Nuovo Prodotto</Button>
            )}
          </Space>
        </Space>
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={sortedData}
          columns={columns}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} prodotti`,
          }}
          rowSelection={rowSelection}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal
        title={editing ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditing(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText={editing ? 'Salva' : 'Crea'}
        cancelText="Annulla"
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{ is_active: true }}
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}
        >
          <Form.Item name="name" label="Nome prodotto" rules={[{ required: true, message: 'Inserisci il nome prodotto' }]} style={{ gridColumn: '1 / -1' }}>
            <Input placeholder="Nome prodotto" />
          </Form.Item>
          <Form.Item name="is_active" label="Attivo" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <ItemDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        item={selectedItem}
        itemType="chemical"
      />
    </MainLayout>
  );
}

export default Chemicals;
